/* eslint-disable react-hooks/exhaustive-deps */
import {useRef, useEffect, useReducer, useCallback, useState, useMemo} from "react"
import moment from "moment"
import {useTranslation} from "react-i18next"
import {useHistory, useLocation} from "react-router-dom"
import {get, keyBy, uniqBy, isEmpty, head, groupBy, findIndex, size, last} from "lodash"
import QuickPinchZoom, {make2dTransformValue, make3dTransformValue} from "react-quick-pinch-zoom"
import {BrowserView, MobileView, isSafari} from "react-device-detect"
import {useWindowSize, useUpdateEffect} from "react-use"
import {Icon} from "components/Icon"
import {BaseButton, SecondaryButton} from "components/buttons"
import {BaseLoading} from "components/Loading"
import {CreatedInfo} from "components/ui"
import HTMLTitle from "components/misc/HTMLTitle"
import {handleError, isAnswerProcessingError, loadImage, toastError, toastSuccess, hasTranslate3DSupport} from "helpers"
import {
    DigitalDocumentFieldSetUp,
    TaskType,
    ParticipantType,
    InitialType,
    SignatureMethod,
    SelectStaffInstructorType,
    TaskStatus
} from "types/tasks"
import {SignaturePopup, InitialsPopup, ConfirmPopup} from "uiKit/popup"
import {useModel, useMutation, useSearchParam, useVisible} from "hooks"
import {taskService, userService} from "services"
import {RejectTaskPopup} from "../RejectTaskPopup"
import {TaskProcessing} from "../TaskProcessing"
import {useTaskUserPolling} from "../../../hooks/useTaskUserPolling"
import {FieldItem} from "./FieldItem"
import styles from "./TaskSubmission.module.css"
import {Iframe} from "components/Iframe"
import {stringifyUrl} from "query-string"
import cx from "classnames"
import {Closing} from "sections/Closing"
import {Auth} from "types/auth"
import {Checkbox} from "antd"
import {useCalculateFields} from "./useCalculateFields"
import {useSubmitTask} from "./useSubmitTask"
import {CustomDigitalActions} from "./CustomDigitalActions"

const makeTransformValue = hasTranslate3DSupport() && !isSafari ? make3dTransformValue : make2dTransformValue

function reducer(state, action) {
    return {...state, ...action}
}

const sendIframeObject = {
    type: "",
    data: {}
}

const DISABLE_BUTTON_FOR_WEB_FORMS = [
    "asher",
    "asher_enrollment_transfer",
    "asher_main_proposal_form",
    "turnagegroup_contract_packet",
    "northstar_contract_packet",
    "continental_enrollment_packet_new_version",
    "memphis_contract_packet",
    "gic_contract_packet"
]

const REMIND_TYPES = [
    {label: "Student", value: Auth.UserProfileType.Student},
    {label: "Instructor", value: SelectStaffInstructorType.Instructor},
    {label: "Lead Instructor", value: SelectStaffInstructorType.LeadInstructor}
]

export function TaskSubmission(props) {
    const {
        taskData,
        user,
        subTaskStatus,
        subTaskAnswers,
        taskUserId,
        taskUserEncodedSecurityLevelWeb,
        taskUsers,
        canSign,
        currentSignature,
        currentInitials,
        subTaskId,
        signerId,
        onSetNewSignature,
        onSetNewInitials,
        canApproveCustomTask,
        isLeadInstructor,
        isInstructor,
        hasInstructorSigner,
        hasLeadInstructorSigner,
        processingTaskUser,
        onReload
    } = props
    const location = useLocation()
    const model = useModel()
    const pageRef = useRef(null)
    const pagesRef = useRef(null)
    const iframeRef = useRef<HTMLIFrameElement>()
    const fieldRefs = useRef<Record<string, HTMLDivElement>>({})
    const [remarksData, setRemarksData] = useState(null)
    const {value: relatedTaskIds} = useSearchParam("relatedTaskIds", {
        parse: (value) => value.split(",").map((item) => +item)
    })
    const backUrl = get(location, "state.backUrl")
    const confirmPopup = useVisible(false)
    const initialState = {
        fields: [],
        isLoading: false,
        isSubmitting: false,
        isLoadingIframe: true,
        webFormsUrl: `${process.env.REACT_APP_WEBFORMS_BASE_URL}/sign`,
        webFormSignatureField: "",
        webFormSignatureMethod: SignatureMethod.BOTH,
        webFormInitialsField: "",
        webFormInitialsMethod: SignatureMethod.BOTH,
        showSubmitButton: false,
        disableSubmitButton: DISABLE_BUTTON_FOR_WEB_FORMS.includes(taskData.form?.code)
    }
    const [
        {
            isLoading,
            fields,
            isSubmitting,
            isLoadingIframe,
            webFormsUrl,
            webFormSignatureField,
            webFormSignatureMethod,
            webFormInitialsField,
            webFormInitialsMethod,
            showSubmitButton,
            disableSubmitButton
        },
        dispatch
    ] = useReducer(reducer, initialState)
    const [tempField, setTempField] = useState(null)
    const [processing, setProcessing] = useState<{taskUserId: number; subTaskId?: number; already?: boolean} | null>(
        processingTaskUser || null
    )
    const [errorFieldIds, setErrorFieldIds] = useState([])
    const [remindUserTypes, setRemindUserTypes] = useState([])
    const signaturePopup = useVisible(false)
    const initialsPopup = useVisible(false)
    const rejectTaskPopup = useVisible(false)
    const {t} = useTranslation(["user", "tasks"])
    const history = useHistory()
    const {width: windowWidth} = useWindowSize()
    let url = `${webFormsUrl}/${taskUserId}/?token=${model.authToken}&profileId=${model.profileId}`
    if (model.isSubmittingTaskFromEmail) {
        url = url + `&sl=${model.securityLevelEncoded}`
    } else {
        url = url + `&sl=${taskUserEncodedSecurityLevelWeb}`
    }
    const dateFormat = model.getUserDateFormat()
    const isWebForm = taskData.type === TaskType.FORM
    const isCustomTask = taskData.type === TaskType.CUSTOM
    const isCustomDigitalTask = taskData.type === TaskType.CUSTOM_DD
    const hasApproved = isCustomDigitalTask && taskData.jsonData.isApproved
    const remindTypes = REMIND_TYPES.filter((item) => {
        if (item.value === SelectStaffInstructorType.Instructor) {
            return hasInstructorSigner
        }
        if (item.value === SelectStaffInstructorType.LeadInstructor) {
            return hasLeadInstructorSigner
        }
        return true
    })
    const {calculateFields, scalePlaceInformation, getScaleInfo, margin} = useCalculateFields()
    const {getAnswer, validateFieldBeforeSubmit} = useSubmitTask()
    const {isChecked, isTimedOut, refresh} = useTaskUserPolling({
        taskId: taskData.id,
        subTaskId: processing?.subTaskId,
        taskUserId: processing?.taskUserId,
        enabled: !!processing,
        onSettled: (status) => onProcessingSettled(status)
    })
    const fieldsGroupByParentId = groupBy(fields, "parentId")
    const hasRequiredField = fields.some((field) => {
        if (
            (field.type === DigitalDocumentFieldSetUp.CHECKBOX || field.type === DigitalDocumentFieldSetUp.INITIAL) &&
            field.isYourField &&
            field.required
        ) {
            const hasSigned = (fieldsGroupByParentId[field.parentId] || []).some(
                (fieldItem) => fieldItem.isSigned && fieldItem.value
            )
            return !hasSigned
        }

        return field.isYourField && field.required && !field.value
    })

    const {mutateAsync: approveCustomDigitalTask, isLoading: isApproving} = useMutation(
        () => taskService.approveCustomDigitalTask({taskId: taskData.id}),
        {
            onSuccess: () => {
                toastSuccess("Approve successfully", "", {duration: 1})
                if (isEmpty(relatedTaskIds)) {
                    setTimeout(() => {
                        onClickCancel("The task has been submitted successfully.", true)
                    }, 1800)
                }
            },
            onError: (error) => handleError(error)
        }
    )
    const {mutateAsync: remindCustomDigitalTask} = useMutation(
        (payload: {taskId: number; type: string[]}) => taskService.remindCustomDigitalTask(payload),
        {
            onSuccess: () => {
                toastSuccess("Remind successfully")
            },
            onError: (error) => handleError(error)
        }
    )

    const signatureFields = useMemo(() => {
        return fields.filter((field) => field.type === DigitalDocumentFieldSetUp.SIGNATURE)
    }, [fields])

    const isShowSignSignature = useMemo(() => {
        if (isWebForm) {
            return (
                !webFormSignatureMethod ||
                webFormSignatureMethod === SignatureMethod.HAND ||
                webFormSignatureMethod === SignatureMethod.BOTH
            )
        }
        if (isEmpty(signatureFields)) {
            return true
        }
        return signatureFields.some(
            (field) =>
                !field.signatureMethod ||
                field.signatureMethod === SignatureMethod.HAND ||
                field.signatureMethod === SignatureMethod.BOTH
        )
    }, [signatureFields, isWebForm, webFormSignatureMethod])

    const isShowTextSignature = useMemo(() => {
        if (isWebForm) {
            return (
                !webFormSignatureMethod ||
                webFormSignatureMethod === SignatureMethod.KEYBOARD ||
                webFormSignatureMethod === SignatureMethod.BOTH
            )
        }
        if (isEmpty(signatureFields)) {
            return true
        }
        return signatureFields.some(
            (field) =>
                !field.signatureMethod ||
                field.signatureMethod === SignatureMethod.KEYBOARD ||
                field.signatureMethod === SignatureMethod.BOTH
        )
    }, [signatureFields, isWebForm, webFormSignatureMethod])

    const isShowSignInitials = useMemo(() => {
        if (!isWebForm) {
            return true
        }
        return (
            !webFormInitialsMethod ||
            webFormInitialsMethod === SignatureMethod.HAND ||
            webFormInitialsMethod === SignatureMethod.BOTH
        )
    }, [isWebForm, webFormInitialsMethod])

    const isShowTextInitials = useMemo(() => {
        if (!isWebForm) {
            return true
        }
        return (
            !webFormInitialsMethod ||
            webFormInitialsMethod === SignatureMethod.KEYBOARD ||
            webFormInitialsMethod === SignatureMethod.BOTH
        )
    }, [isWebForm, webFormInitialsMethod])

    useEffect(() => {
        if (!isWebForm && !isCustomTask) {
            initFieldData()
        }
    }, [])

    useEffect(() => {
        if (isWebForm) {
            const handler = (e) => {
                try {
                    const data = JSON.parse(e.data)
                    if (data?.type === "field" && data?.data.field === "signature") {
                        updateSignature(data.data.url)
                    }
                    if (data?.type === "field" && data?.data.field === "initials") {
                        updateInitials(data.data.url)
                    }
                    if (!data?.error && data.message === "submit") {
                        const url = stringifyUrl(
                            {
                                url: `/tasks/detail`,
                                query: {
                                    id: taskData.id,
                                    type: taskData.type
                                }
                            },
                            {skipNull: true}
                        )
                        history.push(url)
                    }
                    if (data?.type === "action" && data?.message === "create_signature") {
                        dispatch({webFormSignatureField: data?.field, webFormSignatureMethod: data?.signatureMethod})
                        signaturePopup.open()
                    }
                    if (data?.type === "action" && data?.message === "create_initials") {
                        dispatch({webFormInitialsField: data?.field, webFormInitialsMethod: data?.signatureMethod})
                        initialsPopup.open()
                    }
                    if (data?.type === "action" && data?.message === "show_submit_button") {
                        dispatch({showSubmitButton: true})
                    }
                    if (data?.type === "action" && data?.message === "end_submitting") {
                        dispatch({isSubmitting: false})
                    }
                    if (data?.type === "action" && data?.message === "end_submitting_task") {
                        dispatch({isSubmitting: false})
                        setProcessing({taskUserId, subTaskId: currentSubTaskId})
                    }
                    if (data?.type === "action" && data?.message === "enable_submit_button") {
                        dispatch({disableSubmitButton: false})
                    }
                    if (data?.type === "action" && data?.message === "open_link") {
                        window.open(data?.url, "_blank")
                    }
                } catch (e) {
                    return () => e
                }
            }
            window.addEventListener("message", handler)
            return () => window.removeEventListener("message", handler)
        }
        if (isCustomTask) {
            const foundFirstTaskUserToSign = taskUsers.find(
                (tu) => [TaskStatus.TODO, TaskStatus.OVERDUE].includes(tu.status) && tu.userId === user.id
            )
            const customTaskUrl = foundFirstTaskUserToSign?.customSignUrl || ""
            if (customTaskUrl) {
                const newCustomTaskUrl = new URL(customTaskUrl)
                newCustomTaskUrl.searchParams.append("sl", taskUserEncodedSecurityLevelWeb)
                window.open(newCustomTaskUrl.toString())
                onClickCancel()
            } else {
                toastError("Sign url not found for this custom task.")
                onClickCancel()
            }
        }
    }, [])

    // Ignore height. The mobile keyboard changes it, and recalculating then re-renders
    // the pinch-zoomed page while an input is focused.
    useUpdateEffect(() => {
        if (!isWebForm && !isCustomTask) {
            const newFields = recalculateFieldsPosition()
            dispatch({fields: newFields})
        }
    }, [windowWidth])

    const updateSignature = async (trimmedDataURL) => {
        dispatch({isLoading: true})
        try {
            const {data} = await userService.updateCurrentSignatureUsingBase64Image(trimmedDataURL)
            onSetNewSignature(data)
            toastSuccess(t("personal.signature.saveSignature"))
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const updateInitials = async (trimmedDataURL) => {
        dispatch({isLoading: true})
        try {
            const {data} = await userService.updateCurrentInitialsUsingBase64Image(trimmedDataURL)
            onSetNewInitials(data)
            toastSuccess(t("personal.signature.saveInitials"))
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const handleApproveCustomTask = async () => {
        await onClickSubmit()
        await approveCustomDigitalTask()
    }

    const sendSignatureToIframe = async () => {
        sendIframeObject.type = "signature"
        let base64Img = ""
        if (currentSignature?.imageUrl) {
            try {
                const images = await userService.getBase64Img([currentSignature.imageUrl])
                base64Img = get(images, "[0].base64", "")
            } catch (error) {
                handleError(error)
            }
        }
        sendIframeObject.data = {
            field: webFormSignatureField,
            url: base64Img
        }
        iframeRef?.current?.contentWindow.postMessage(sendIframeObject, "*")
    }

    const sendInitialsToIframe = async () => {
        sendIframeObject.type = "initials"
        let base64Img = ""
        if (currentInitials?.imageUrl) {
            try {
                const images = await userService.getBase64Img([currentInitials.imageUrl])
                base64Img = get(images, "[0].base64", "")
            } catch (error) {
                handleError(error)
            }
        }
        sendIframeObject.data = {
            field: webFormInitialsField,
            url: base64Img
        }
        iframeRef?.current?.contentWindow.postMessage(sendIframeObject, "*")
    }

    useEffect(() => {
        if (isWebForm && currentSignature) {
            sendSignatureToIframe()
        }
    }, [currentSignature])

    useEffect(() => {
        if (isWebForm && currentInitials) {
            sendInitialsToIframe()
        }
    }, [currentInitials])

    useEffect(() => {
        if (isWebForm) {
            const taskUser = taskUsers.find((item) => item.id === taskUserId)
            setRemarksData({remarkedReason: taskUser?.remarkedReason})
        }
    }, [taskUsers])

    const initFieldData = async () => {
        try {
            dispatch({isLoading: true})
            await Promise.all(taskData.digitalDocument.pages.map((image) => loadImage(image)))
            const usersData = await getStudentData()
            const sortedFields = calculateFields({
                usersData,
                taskUsers,
                taskUserId,
                taskData,
                pageRef,
                signerId,
                isLeadInstructor,
                isInstructor,
                subTaskAnswers,
                canSign,
                setRemarksData
            })
            dispatch({fields: sortedFields})
        } catch (error) {
            console.log("error", error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const recalculateFieldsPosition = () => {
        const {scaleHeight, scaleWidth, pageHeight} = getScaleInfo({taskData, pageRef})

        const newFields = fields.map((field) => {
            if (field.originalPlaceInformation) {
                const {height, left, top, width} = field.originalPlaceInformation

                field.placeInformation = scalePlaceInformation(
                    {height, left, top, width},
                    {scaleHeight, scaleWidth},
                    false
                )
                field.placeInformation.top = field.placeInformation.top + (field.page - 1) * (pageHeight + margin)
            }

            return field
        })

        return newFields
    }

    const getStudentData = async () => {
        try {
            const signersKeyById = keyBy(taskData.digitalDocument.signers, "id")
            const taskUsersKeyBySignerId = keyBy(taskUsers, "signerId")
            const taskUserDataOnlyKeyBySubTaskId = keyBy(taskData.digitalDocument.taskUserDataOnly || [], "subtaskId")

            let userParticipants = taskData.digitalDocument.fields
                .filter((field) => field.type === DigitalDocumentFieldSetUp.TEXT && field.defaultParticipant)
                .map((field) => {
                    const participantType = get(signersKeyById, [+field.defaultParticipant, "participantType"], "")
                    let userId = null
                    let profileId = null
                    if (participantType === ParticipantType.UserDataOnly) {
                        const taskUserDataOnly = head(taskData.digitalDocument.taskUserDataOnly)
                        userId = isCustomDigitalTask
                            ? get(taskUserDataOnly, "userId")
                            : get(taskUserDataOnlyKeyBySubTaskId, [subTaskId, "userId"], "")
                        profileId = isCustomDigitalTask
                            ? get(taskUserDataOnly, "profileId")
                            : get(taskUserDataOnlyKeyBySubTaskId, [subTaskId, "profileId"], "")
                    } else {
                        profileId = get(taskUsersKeyBySignerId, [field.defaultParticipant, "profileId"], "")
                        userId = get(taskUsersKeyBySignerId, [field.defaultParticipant, "userId"], "")
                    }
                    return {userId, participantId: field.defaultParticipant, profileId}
                })
                .filter(({userId}) => userId)

            userParticipants = uniqBy(userParticipants, "userId")
            const profileIds = userParticipants.map(({profileId}) => profileId)
            if (isEmpty(profileIds)) {
                return []
            }

            const data = await userService.getStudentData({filter: {profileId: profileIds}})
            const userParticipantByProfileId = keyBy(userParticipants, "profileId")
            const result = data.map((item) => {
                item.participantId = get(userParticipantByProfileId[item.profileId], "participantId")
                return item
            })

            return result
        } catch (error) {
            handleError(error)
        }
    }

    const onChangeField = (fieldId, newValue) => {
        const newFields = fields.map((field) => {
            if (field.id === fieldId) {
                return {...field, ...newValue}
            }
            if (
                field.parentId === fieldId &&
                field.type === DigitalDocumentFieldSetUp.DATE_FIELD &&
                newValue.isSignature
            ) {
                return {
                    ...field,
                    isSigned: newValue.isSigned,
                    date: newValue.isSigned ? moment().format(dateFormat) : null
                }
            }
            return field
        })
        dispatch({fields: newFields})
    }

    const onChangeSingleCheckboxField = ({fieldId, parentId}, newValue) => {
        const newFields = fields.map((field) => {
            if (field.id === fieldId) {
                return {...field, ...newValue}
            }
            if (parentId) {
                if (field.id === parentId || field.parentId === parentId) {
                    return {...field, ...newValue, value: false}
                }
            } else if (field.parentId === fieldId) {
                return {...field, ...newValue, value: false}
            }
            return field
        })
        dispatch({fields: newFields})
    }

    const onChangeSingleInitialsField = ({fieldId, parentId}, newValue) => {
        const newFields = fields.map((field) => {
            if (field.id === fieldId) {
                return {...field, ...newValue}
            }
            if (parentId) {
                if (field.id === parentId || field.parentId === parentId) {
                    return {...field, ...newValue, value: null}
                }
            } else if (field.parentId === fieldId) {
                return {...field, ...newValue, value: null}
            }
            return field
        })
        dispatch({fields: newFields})
    }

    const onClickCancel = (text = `The task was not answered`, isSubmitted = false) => {
        if (model.isSubmittingTaskFromEmail) {
            history.push(`/closing`, {
                text,
                closeSessionStorage: true,
                closeLocalStorage: false
            })
        } else if (isCustomDigitalTask) {
            const hasChanges = !isSubmitted && fields.some((field) => field.value !== field.originalValue)
            if (hasChanges) {
                confirmPopup.open()
            } else {
                handleBackCustomDDTask()
            }
        } else {
            const backUrl = get(location.state, "backUrl")
            history.push({
                pathname: `/tasks/detail`,
                search: `type=${taskData.type}&id=${taskData.id}`,
                state: {backUrl}
            })
        }
    }

    const onConfirmSaveAndExit = async () => {
        if (hasRequiredField) {
            confirmPopup.close()
        } else {
            await onClickSubmit()
            confirmPopup.close()
            handleBackCustomDDTask()
        }
    }

    const handleBackCustomDDTask = () => {
        const backUrl = get(location, "state.backUrl")
        if (backUrl && !isEmpty(relatedTaskIds)) {
            history.push(backUrl)
        } else {
            history.goBack()
        }
    }

    const currentSubTaskId = taskUsers.find((item) => item.id === taskUserId)?.subTaskId || +subTaskId || undefined

    const onClickRejectTask = () => {
        rejectTaskPopup.open()
    }

    const onCloseRejectTask = () => {
        rejectTaskPopup.close()
        if (model.isSubmittingTaskFromEmail) {
            history.push(`/closing`, {
                closeSessionStorage: true,
                closeLocalStorage: false
            })
        }
    }

    const getNextRelatedTaskId = () => {
        const idx = findIndex(relatedTaskIds, (item) => item === taskData.id)
        if (idx < size(relatedTaskIds) - 1) {
            return relatedTaskIds[idx + 1]
        }
        return head(relatedTaskIds)
    }

    const onAnswered = () => {
        toastSuccess("Submit successful", "", {duration: 1})
        if (isEmpty(relatedTaskIds)) {
            setTimeout(() => {
                onClickCancel("The task has been submitted successfully.", true)
            }, 1800)
        } else {
            history.push({
                pathname: `/tasks/submissions`,
                search: `id=${getNextRelatedTaskId()}&type=customDD&relatedTaskIds=${relatedTaskIds}`,
                state: {backUrl}
            })
        }
    }

    const onProcessingSettled = (status: TaskStatus) => {
        if ([TaskStatus.TODO, TaskStatus.OVERDUE].includes(status)) {
            setProcessing(null)
            toastError(t("tasks:tasks.processingFailed"))
            // opened while processing: there is no signable task user yet, reload to get it
            if (!taskUserId && onReload) {
                onReload()
            }
            return
        }
        // keep the processing view until onAnswered navigates away, so the stale form is not shown again
        onAnswered()
    }

    const onClickSubmit = async () => {
        if (isLoading) {
            return
        }
        const hasError = validateFieldBeforeSubmit({fields, fieldRefs, setErrorFieldIds})
        if (hasError) {
            return
        }
        try {
            dispatch({isSubmitting: true})
            const {answers, hasSignature, hasInitials} = await getAnswer({fields, currentSignature, currentInitials})
            if ((hasSignature && !answers.signature) || (hasInitials && !answers.initials)) {
                return
            }
            try {
                let params = `sl=${taskUserEncodedSecurityLevelWeb}`
                if (model.isSubmittingTaskFromEmail) {
                    params = `sl=${model.securityLevelEncoded}`
                }
                if (isCustomDigitalTask) {
                    await taskService.answerCustomDigitalTask({taskId: taskData.id, answers}, params)
                } else {
                    await taskService.answerTask({taskUserId, answers}, params)
                    // the answer can be processed asynchronously, the polling decides when it is finished
                    setProcessing({taskUserId, subTaskId: currentSubTaskId})
                    return
                }
                onAnswered()
            } catch (error) {
                if (isAnswerProcessingError(error)) {
                    setProcessing({taskUserId, subTaskId: currentSubTaskId, already: true})
                    return
                }
                handleError(error)
            }
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isSubmitting: false})
        }
    }

    const renderTaskHeader = () => {
        return (
            <div
                className={cx(styles.headerWrap, {
                    [styles.headerWrapFromEmail]: model.isSubmittingTaskFromEmail,
                    [styles.webFormHeader]: isWebForm
                })}>
                {!model.isSubmittingTaskFromEmail && (
                    <div className={styles.backBtn}>
                        <div className={styles.backBtnWrap} onClick={() => onClickCancel()}>
                            <Icon icon="BACK" className={styles.backIcon} color="#ff349b" />
                        </div>
                    </div>
                )}
                <div className={styles.headerTitleWrap}>
                    <p className={styles.headerTitle}>{taskData.name}</p>
                    <p className={styles.headerTaskType}>{isWebForm ? "Web Form" : "Digital document task"}</p>
                </div>
                <p className={styles.taskCode}>{taskData.code}</p>
                {isCustomDigitalTask && !isEmpty(relatedTaskIds) && (
                    <CustomDigitalActions taskId={taskData.id} className={styles.customDDActions__end} />
                )}
            </div>
        )
    }

    const renderExtraInfo = () => {
        if (!isCustomDigitalTask) {
            return (
                <>
                    <div className={styles.taskInfoDetailHeaderItem}>
                        <p className={styles.taskInfoDetailHeaderItem__text}>{t("tasks:tasks.dueDate")}</p>
                        <p className={styles.taskInfoDetailHeaderItem__value}>
                            {moment(taskData.dueDate).format(dateFormat)}
                        </p>
                    </div>
                    <div className={styles.taskInfoDetailHeaderItem}>
                        <p className={styles.taskInfoDetailHeaderItem__text}>Status</p>
                        <p className={styles.taskInfoDetailHeaderItem__value}>{t(`tasks:tasks.${subTaskStatus}`)}</p>
                    </div>
                </>
            )
        }
        if (canApproveCustomTask) {
            if (hasApproved) {
                return (
                    <div className={styles.taskInfoDetailHeaderItem}>
                        <span className={styles.approvedText}>Task has been approved</span>
                    </div>
                )
            }
            return (
                <div className={styles.remindWrap}>
                    <Checkbox.Group
                        options={remindTypes}
                        value={remindUserTypes}
                        onChange={(value) => setRemindUserTypes(value)}
                    />
                    <BaseButton
                        title="Remind"
                        variant="secondary"
                        disabled={isEmpty(remindUserTypes)}
                        onClick={() => remindCustomDigitalTask({taskId: taskData.id, type: remindUserTypes})}
                    />
                </div>
            )
        }
    }

    const renderTaskInfo = () => {
        const sendIframe = (e) => {
            sendIframeObject.type = "action"
            sendIframeObject.data = "submit"
            if (isWebForm) {
                iframeRef.current.contentWindow.postMessage(sendIframeObject, "*")
                dispatch({isSubmitting: true})
            }
        }

        const renderSubmitButton = () => {
            if (isWebForm && taskData.form.code === "nau_isir_corrections_2023" && !showSubmitButton) {
                return
            }
            return (
                <BaseButton
                    title={isEmpty(relatedTaskIds) ? "Submit" : "Save"}
                    onClick={isWebForm ? sendIframe : onClickSubmit}
                    loading={isSubmitting}
                    className={styles.taskInfoActionItem}
                    isActive={!isLoading}
                    disabled={disableSubmitButton || (isCustomDigitalTask && hasRequiredField)}
                />
            )
        }

        return (
            <div
                className={cx(styles.taskInfoWrap, {
                    [styles.taskInfoWrapFromEmail]: model.isSubmittingTaskFromEmail,
                    [styles.webFormHeader]: isWebForm
                })}>
                <div className={styles.taskInfo}>
                    <div className={styles.taskInfoManage}>
                        <p className={styles.taskInfoManage__title}>Manage</p>
                        <div className={styles.taskInfoManageAction}>
                            <div
                                className={styles.taskInfoManageAction__item}
                                onClick={() => {
                                    dispatch({webFormSignatureField: "", webFormSignatureMethod: SignatureMethod.BOTH})
                                    signaturePopup.open()
                                }}>
                                <Icon icon="SIGN_HAND_DRAWN" color="#666" />
                                <p className={styles.taskInfoManageAction__item__text}>SIGNATURE</p>
                            </div>
                            <div
                                className={styles.taskInfoManageAction__item}
                                onClick={() => {
                                    dispatch({webFormInitialsField: "", webFormInitialsMethod: SignatureMethod.BOTH})
                                    initialsPopup.open()
                                }}>
                                <Icon icon="INITIALS" color="#666" />
                                <p className={styles.taskInfoManageAction__item__text}>INITIALS</p>
                            </div>
                        </div>
                    </div>
                    <div className={styles.taskInfoDetail}>
                        <div className={styles.taskInfoDetailHeader}>
                            <div className={styles.taskInfoDetailHeaderItem}>
                                <p className={styles.taskInfoDetailHeaderItem__text}>Requested</p>
                                <p className={styles.taskInfoDetailHeaderItem__value}>
                                    {moment(taskData.createdAt).format(dateFormat)}
                                </p>
                            </div>
                            {renderExtraInfo()}
                            {canSign && !processing && (
                                <div className={styles.taskInfoAction}>
                                    <SecondaryButton
                                        title="Cancel"
                                        className={styles.taskInfoActionItem}
                                        onClick={() => onClickCancel()}
                                        loading={isSubmitting}
                                    />
                                    {!isCustomDigitalTask && (
                                        <BaseButton
                                            title="Reject"
                                            className={styles.taskInfoActionItem}
                                            onClick={onClickRejectTask}
                                            isActive={!isLoading}
                                            loading={isSubmitting}
                                        />
                                    )}
                                    {renderSubmitButton()}
                                    {isCustomDigitalTask && canApproveCustomTask && (
                                        <BaseButton
                                            title="Approve"
                                            className={styles.taskInfoActionItem}
                                            onClick={handleApproveCustomTask}
                                            isActive={!isLoading}
                                            loading={isApproving || isSubmitting}
                                            disabled={hasRequiredField}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <CreatedInfo data={taskData} userInfoKey="createdByUser" />
                {remarksData?.remarkedReason && (
                    <p className={styles.reopenText}>
                        Reopen Reason: <strong className={styles.reasonContent}>{remarksData?.remarkedReason}</strong>
                    </p>
                )}
            </div>
        )
    }

    const onLoadIframe = async () => {
        const promises = []
        if (currentSignature) {
            promises.push(await sendSignatureToIframe())
        }
        if (currentInitials) {
            promises.push(await sendInitialsToIframe())
        }
        await Promise.all(promises)
        dispatch({isLoadingIframe: false})
    }

    const onZoomUpdate = useCallback(({x, y, scale}) => {
        if (pagesRef.current) {
            const value = makeTransformValue({x, y, scale})
            pagesRef.current.style.setProperty("transform", value)
        }
    }, [])

    const onClickSignatureFieldWithoutData = (field) => {
        setTempField(field)
        signaturePopup.open()
    }

    const onSaveNewSignature = (newSignature) => {
        if (tempField) {
            onChangeField(tempField.id, {
                isSigned: true,
                value: newSignature.imageUrl,
                isSignature: true
            })
            setTempField(null)
        }
        onSetNewSignature(newSignature)
    }

    const onClickInitialFieldWithoutData = (field) => {
        setTempField(field)
        initialsPopup.open()
    }

    const onSaveNewInitials = (newInitials) => {
        if (tempField) {
            const value = newInitials.imageUrl
            if (tempField.initialType === InitialType.MULTIPLE) {
                onChangeField(tempField.id, {isSigned: true, value})
            } else {
                onChangeSingleInitialsField(
                    {fieldId: tempField.id, parentId: tempField.parentId},
                    {isSigned: true, value}
                )
            }
            setTempField(null)
        }
        onSetNewInitials(newInitials)
    }

    const renderContent = () => {
        return (
            <div className={styles.pageWrap} ref={pagesRef}>
                {(taskData.digitalDocument.pages || []).map((page, index) => (
                    <img
                        ref={pageRef}
                        key={index}
                        className={cx(styles.pageImg, {
                            [styles.lastPage]: index === taskData.digitalDocument.pages.length - 1
                        })}
                        src={page.url}
                        style={{maxWidth: window.innerWidth}}
                        alt=""
                    />
                ))}
                {fields.map((field, index) => (
                    <FieldItem
                        key={index}
                        field={field}
                        fields={fields}
                        currentSignature={currentSignature}
                        currentInitials={currentInitials}
                        errorFieldIds={errorFieldIds}
                        fieldRefs={fieldRefs}
                        openSignaturePopup={onClickSignatureFieldWithoutData}
                        openInitialPopup={onClickInitialFieldWithoutData}
                        canSign={canSign}
                        onChangeField={onChangeField}
                        onChangeSingleCheckboxField={onChangeSingleCheckboxField}
                        onChangeSingleInitialsField={onChangeSingleInitialsField}
                    />
                ))}
            </div>
        )
    }

    return (
        <div className={styles.wrap}>
            <HTMLTitle title={t("tasks:tasks.taskSubmissions")} />
            <div className={styles.headerContainer}>
                <BaseLoading isShow={isLoading || (!!processing && !isChecked)} />
                <div style={{height: 12}}></div>
                {renderTaskHeader()}
                {renderTaskInfo()}
            </div>
            {processing ? (
                // the first check decides if the answer is already finished (sync flow), so nothing flashes
                isChecked && (
                    <TaskProcessing
                        isTimedOut={isTimedOut}
                        isAlreadyProcessing={processing.already}
                        onRefresh={refresh}
                    />
                )
            ) : isWebForm ? (
                taskUserId ? (
                    <>
                        <Iframe
                            id="web_forms"
                            className={styles.iframeWebForm}
                            iframe_url={url}
                            title={t("tasks:taskDetail.type.form")}
                            onLoad={onLoadIframe}
                            iframeRef={iframeRef}
                        />
                        <BaseLoading isShow={isLoadingIframe} />
                    </>
                ) : (
                    <Closing model={model} />
                )
            ) : (
                !isCustomTask && (
                    <div className={styles.body}>
                        <BrowserView>{renderContent()}</BrowserView>
                        <MobileView>
                            <QuickPinchZoom
                                minZoom={1}
                                onUpdate={onZoomUpdate}
                                containerProps={{style: {touchAction: "auto"}}}
                                draggableUnZoomed={false}>
                                {renderContent()}
                            </QuickPinchZoom>
                        </MobileView>
                    </div>
                )
            )}
            {signaturePopup.isVisible && (
                <SignaturePopup
                    isShow={signaturePopup.isVisible}
                    isShowSign={isShowSignSignature}
                    isShowText={isShowTextSignature}
                    onClose={() => signaturePopup.close()}
                    title={t("personal.signature.signature")}
                    onSetNewSignature={onSaveNewSignature}
                    currentSignatureLoaded={currentSignature}
                    isAnsweringATask
                />
            )}
            {initialsPopup.isVisible && (
                <InitialsPopup
                    isShow={initialsPopup.isVisible}
                    isShowSign={isShowSignInitials}
                    isShowText={isShowTextInitials}
                    onClose={() => initialsPopup.close()}
                    title={t("personal.initials.initials")}
                    onSetNewInitials={onSaveNewInitials}
                    currentInitialLoaded={currentInitials}
                    isAnsweringATask
                />
            )}
            <RejectTaskPopup
                isShow={rejectTaskPopup.isVisible}
                onClose={onCloseRejectTask}
                onProcessing={() => {
                    rejectTaskPopup.close()
                    setProcessing({taskUserId, subTaskId: currentSubTaskId, already: true})
                }}
                taskUserIds={[taskUserId]}
                taskData={taskData}
                subTaskId={subTaskId}
            />
            <ConfirmPopup
                isVisible={confirmPopup.isVisible}
                onClose={() => {
                    handleBackCustomDDTask()
                    confirmPopup.close()
                }}
                onConfirm={onConfirmSaveAndExit}
                title="Exit Task"
                description="Are you sure you want to exit the task? The changes you made will not be saved."
                icon="WARNING_SOLID"
                cancelLabel="Exit"
                confirmLabel={hasRequiredField ? "Continue Updating" : "Save & Exit"}
                loading={isSubmitting}
            />
        </div>
    )
}
